import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useUser } from '../context/UserContext';
import api from '../services/api';
import socketService from '../services/socket';

const Admin = () => {
  const { user } = useUser();
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState('dashboard');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  
  // Dashboard data
  const [dashboardData, setDashboardData] = useState(null);
  
  // Reports data
  const [reports, setReports] = useState([]);
  const [reportsPagination, setReportsPagination] = useState({});
  const [reportsPage, setReportsPage] = useState(1);
  const [reportsStatus, setReportsStatus] = useState('');
  
  // Users data
  const [users, setUsers] = useState([]);
  const [usersPagination, setUsersPagination] = useState({});
  const [usersPage, setUsersPage] = useState(1);
  const [usersSearch, setUsersSearch] = useState('');
  
  // Activity data
  const [activity, setActivity] = useState({ reports: [], newUsers: [] });

  // Feedback data
  const [feedback, setFeedback] = useState([]);
  const [feedbackPagination, setFeedbackPagination] = useState({});
  const [feedbackPage, setFeedbackPage] = useState(1);
  const [feedbackStatus, setFeedbackStatus] = useState('');
  const [feedbackCategory, setFeedbackCategory] = useState('');
  const [socketConnected, setSocketConnected] = useState(false);

  useEffect(() => {
    if (!user) {
      navigate('/login');
      return;
    }

    // Check if user is admin
    checkAdminAccess();
    
    // Initialize Socket.IO connection for admin updates
    initializeSocket();
    
    return () => {
      // Cleanup socket connection
      socketService.disconnect();
    };
  }, [user, navigate]);

  const checkAdminAccess = async () => {
    try {
      setLoading(true);
      await api.getAdminDashboard();
      setLoading(false);
    } catch (error) {
      console.error('Admin access check failed:', error);
      setError('Access denied. Admin privileges required.');
      setLoading(false);
    }
  };

  const initializeSocket = async () => {
    try {
      const token = localStorage.getItem('token');
      if (token) {
        await socketService.connect(token);
        setSocketConnected(true);
        
        // Join admin room for real-time updates
        socketService.socket.emit('join_admin_room');
        
        // Listen for new reports
        socketService.socket.on('new_report', (data) => {
          console.log('🚨 New report received:', data);
          // Refresh reports list if we're on the reports tab
          if (activeTab === 'reports') {
            loadReports();
          }
          // Refresh dashboard stats
          if (activeTab === 'dashboard') {
            loadDashboard();
          }
        });
        
        // Listen for report updates
        socketService.socket.on('report_updated', (data) => {
          console.log('📝 Report updated:', data);
          // Refresh reports list if we're on the reports tab
          if (activeTab === 'reports') {
            loadReports();
          }
        });
        
        // Listen for dashboard stats updates
        socketService.socket.on('dashboard_stats_updated', (data) => {
          console.log('📊 Dashboard stats updated:', data);
          // Update dashboard stats in real-time
          if (activeTab === 'dashboard' && dashboardData) {
            setDashboardData(prev => ({
              ...prev,
              stats: {
                ...prev.stats,
                totalReports: data.totalReports,
                pendingReports: data.pendingReports
              }
            }));
          }
        });
        
        // Listen for admin notifications
        socketService.socket.on('admin_notification', (data) => {
          console.log('🔔 Admin notification:', data);
          // You can add a toast notification here if needed
        });
        
        // Listen for connection status
        socketService.socket.on('disconnect', () => {
          setSocketConnected(false);
        });
        
        socketService.socket.on('connect', () => {
          setSocketConnected(true);
        });
        
      }
    } catch (error) {
      console.error('Failed to initialize admin socket:', error);
      setSocketConnected(false);
    }
  };

  const loadDashboard = async () => {
    try {
      const response = await api.getAdminDashboard();
      setDashboardData(response.data);
    } catch (error) {
      console.error('Failed to load dashboard:', error);
    }
  };

  const loadReports = async () => {
    try {
      const response = await api.getAdminReports(reportsPage, 20, reportsStatus);
      setReports(response.data.reports);
      setReportsPagination(response.data.pagination);
    } catch (error) {
      console.error('Failed to load reports:', error);
    }
  };

  const loadUsers = async () => {
    try {
      const response = await api.getAdminUsers(usersPage, 20, usersSearch);
      setUsers(response.data.users);
      setUsersPagination(response.data.pagination);
    } catch (error) {
      console.error('Failed to load users:', error);
    }
  };

  const loadActivity = async () => {
    try {
      const response = await api.getAdminActivity(20);
      setActivity(response.data);
    } catch (error) {
      console.error('Failed to load activity:', error);
    }
  };

  const loadFeedback = async () => {
    try {
      console.log('Loading feedback...');
      const response = await api.getAdminFeedback(feedbackPage, 20, feedbackStatus, feedbackCategory);
      console.log('Feedback response:', response);
      setFeedback(response.data.feedback);
      setFeedbackPagination(response.data.pagination);
    } catch (error) {
      console.error('Failed to load feedback:', error);
    }
  };

  const updateReportStatus = async (reportId, status, adminNotes = '') => {
    try {
      await api.updateReportStatus(reportId, status, adminNotes);
      await loadReports(); // Refresh reports
    } catch (error) {
      console.error('Failed to update report status:', error);
    }
  };

  const toggleUserBan = async (userId, banned, reason = '') => {
    try {
      await api.toggleUserBan(userId, banned, reason);
      await loadUsers(); // Refresh users
    } catch (error) {
      console.error('Failed to toggle user ban:', error);
    }
  };

  const updateFeedbackStatus = async (feedbackId, status, adminNotes = '') => {
    try {
      await api.updateFeedbackStatus(feedbackId, status, adminNotes);
      await loadFeedback(); // Refresh feedback
    } catch (error) {
      console.error('Failed to update feedback status:', error);
    }
  };

  useEffect(() => {
    if (activeTab === 'dashboard') {
      loadDashboard();
    } else if (activeTab === 'reports') {
      loadReports();
    } else if (activeTab === 'users') {
      loadUsers();
    } else if (activeTab === 'activity') {
      loadActivity();
    } else if (activeTab === 'feedback') {
      loadFeedback();
    }
  }, [activeTab, reportsPage, reportsStatus, usersPage, usersSearch, feedbackPage, feedbackStatus, feedbackCategory]);

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 dark:bg-gray-900 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
          <p className="mt-4 text-gray-600 dark:text-gray-400">Loading admin dashboard...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gray-50 dark:bg-gray-900 flex items-center justify-center">
        <div className="text-center">
          <div className="text-red-600 text-6xl mb-4">🚫</div>
          <h1 className="text-2xl font-bold text-gray-900 dark:text-white mb-2">Access Denied</h1>
          <p className="text-gray-600 dark:text-gray-400 mb-4">{error}</p>
          <button
            onClick={() => navigate('/')}
            className="bg-blue-600 hover:bg-blue-700 text-white px-6 py-2 rounded-lg"
          >
            Go Home
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900 pt-24">
      {/* Header */}
      <div className="bg-white dark:bg-gray-800 shadow-sm border-b border-gray-200 dark:border-gray-700">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center py-6">
            <div>
              <div className="flex items-center space-x-3">
                <h1 className="text-3xl font-bold text-gray-900 dark:text-white">Admin Dashboard</h1>
                <div className={`flex items-center space-x-2 px-3 py-1 rounded-full text-sm ${
                  socketConnected 
                    ? 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200' 
                    : 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200'
                }`}>
                  <div className={`w-2 h-2 rounded-full ${
                    socketConnected ? 'bg-green-500 animate-pulse' : 'bg-red-500'
                  }`}></div>
                  <span>{socketConnected ? 'Live Updates' : 'Offline'}</span>
                </div>
              </div>
              <p className="text-gray-600 dark:text-gray-400">Manage reports, users, and system</p>
            </div>
            <button
              onClick={() => navigate('/')}
              className="bg-gray-600 hover:bg-gray-700 text-white px-4 py-2 rounded-lg"
            >
              Back to App
            </button>
          </div>
        </div>
      </div>

      {/* Navigation Tabs */}
      <div className="bg-white dark:bg-gray-800 border-b border-gray-200 dark:border-gray-700">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <nav className="flex space-x-8">
            {[
              { id: 'dashboard', name: 'Dashboard', icon: '📊' },
              { id: 'reports', name: 'Reports', icon: '🚨' },
              { id: 'users', name: 'Users', icon: '👥' },
              { id: 'feedback', name: 'Feedback', icon: '💬' },
              { id: 'activity', name: 'Activity', icon: '📈' }
            ].map((tab) => (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`py-4 px-1 border-b-2 font-medium text-sm ${
                  activeTab === tab.id
                    ? 'border-blue-500 text-blue-600 dark:text-blue-400'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300 dark:text-gray-400 dark:hover:text-gray-300'
                }`}
              >
                <span className="mr-2">{tab.icon}</span>
                {tab.name}
              </button>
            ))}
          </nav>
        </div>
      </div>

      {/* Content */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {activeTab === 'dashboard' && (
          <DashboardTab data={dashboardData} />
        )}
        
        {activeTab === 'reports' && (
          <ReportsTab
            reports={reports}
            pagination={reportsPagination}
            page={reportsPage}
            setPage={setReportsPage}
            status={reportsStatus}
            setStatus={setReportsStatus}
            onUpdateStatus={updateReportStatus}
          />
        )}
        
        {activeTab === 'users' && (
          <UsersTab
            users={users}
            pagination={usersPagination}
            page={usersPage}
            setPage={setUsersPage}
            search={usersSearch}
            setSearch={setUsersSearch}
            onToggleBan={toggleUserBan}
          />
        )}
        
        {activeTab === 'feedback' && (
          <FeedbackTab
            feedback={feedback}
            pagination={feedbackPagination}
            page={feedbackPage}
            setPage={setFeedbackPage}
            status={feedbackStatus}
            setStatus={setFeedbackStatus}
            category={feedbackCategory}
            setCategory={setFeedbackCategory}
            onUpdateStatus={updateFeedbackStatus}
          />
        )}
        
        {activeTab === 'activity' && (
          <ActivityTab activity={activity} />
        )}
      </div>
    </div>
  );
};

// Dashboard Tab Component
const DashboardTab = ({ data }) => {
  if (!data) return <div>Loading...</div>;

  const { stats, recentReports, recentUsers } = data;
  
  // Safety checks for data
  if (!stats) return <div>No stats data available</div>;

  return (
    <div className="space-y-6">
      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-6">
        <div className="bg-white dark:bg-gray-800 p-6 rounded-lg shadow">
          <div className="flex items-center">
            <div className="p-3 rounded-full bg-blue-100 dark:bg-blue-900">
              <span className="text-2xl">👥</span>
            </div>
            <div className="ml-4">
              <p className="text-sm font-medium text-gray-600 dark:text-gray-400">Total Users</p>
              <p className="text-2xl font-semibold text-gray-900 dark:text-white">{stats.totalUsers}</p>
            </div>
          </div>
        </div>

        <div className="bg-white dark:bg-gray-800 p-6 rounded-lg shadow">
          <div className="flex items-center">
            <div className="p-3 rounded-full bg-green-100 dark:bg-green-900">
              <span className="text-2xl">✅</span>
            </div>
            <div className="ml-4">
              <p className="text-sm font-medium text-gray-600 dark:text-gray-400">Active Users</p>
              <p className="text-2xl font-semibold text-gray-900 dark:text-white">{stats.activeUsers}</p>
            </div>
          </div>
        </div>

        <div className="bg-white dark:bg-gray-800 p-6 rounded-lg shadow">
          <div className="flex items-center">
            <div className="p-3 rounded-full bg-red-100 dark:bg-red-900">
              <span className="text-2xl">🚨</span>
            </div>
            <div className="ml-4">
              <p className="text-sm font-medium text-gray-600 dark:text-gray-400">Pending Reports</p>
              <p className="text-2xl font-semibold text-gray-900 dark:text-white">{stats.pendingReports}</p>
            </div>
          </div>
        </div>

        <div className="bg-white dark:bg-gray-800 p-6 rounded-lg shadow">
          <div className="flex items-center">
            <div className="p-3 rounded-full bg-orange-100 dark:bg-orange-900">
              <span className="text-2xl">📊</span>
            </div>
            <div className="ml-4">
              <p className="text-sm font-medium text-gray-600 dark:text-gray-400">Total Reports</p>
              <p className="text-2xl font-semibold text-gray-900 dark:text-white">{stats.totalReports}</p>
            </div>
          </div>
        </div>

        <div className="bg-white dark:bg-gray-800 p-6 rounded-lg shadow">
          <div className="flex items-center">
            <div className="p-3 rounded-full bg-purple-100 dark:bg-purple-900">
              <span className="text-2xl">💬</span>
            </div>
            <div className="ml-4">
              <p className="text-sm font-medium text-gray-600 dark:text-gray-400">Pending Feedback</p>
              <p className="text-2xl font-semibold text-gray-900 dark:text-white">{stats.pendingFeedback || 0}</p>
            </div>
          </div>
        </div>
      </div>

      {/* Recent Activity */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <div className="bg-white dark:bg-gray-800 p-6 rounded-lg shadow">
          <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-4">Recent Reports</h3>
          <div className="space-y-3">
            {recentReports && recentReports.slice(0, 5).map((report) => (
              <div key={report._id} className="flex items-center justify-between p-3 bg-gray-50 dark:bg-gray-700 rounded">
                <div>
                  <p className="text-sm font-medium text-gray-900 dark:text-white">
                    Report #{report._id.slice(-6)}
                  </p>
                  <p className="text-xs text-gray-500 dark:text-gray-400">
                    {new Date(report.createdAt).toLocaleDateString()}
                  </p>
                </div>
                <span className={`px-2 py-1 text-xs rounded-full ${
                  report.status === 'pending' ? 'bg-yellow-100 text-yellow-800' :
                  report.status === 'resolved' ? 'bg-green-100 text-green-800' :
                  'bg-gray-100 text-gray-800'
                }`}>
                  {report.status}
                </span>
              </div>
            ))}
          </div>
        </div>

        <div className="bg-white dark:bg-gray-800 p-6 rounded-lg shadow">
          <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-4">New Users</h3>
          <div className="space-y-3">
            {recentUsers && recentUsers.slice(0, 5).map((user) => (
              <div key={user._id} className="flex items-center justify-between p-3 bg-gray-50 dark:bg-gray-700 rounded">
                <div>
                  <p className="text-sm font-medium text-gray-900 dark:text-white">
                    {user.username}
                  </p>
                  <p className="text-xs text-gray-500 dark:text-gray-400">
                    {new Date(user.createdAt).toLocaleDateString()}
                  </p>
                </div>
                <span className="text-xs text-gray-500 dark:text-gray-400">
                  New
                </span>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};

// Reports Tab Component
const ReportsTab = ({ reports, pagination, page, setPage, status, setStatus, onUpdateStatus }) => {
  const [selectedReport, setSelectedReport] = useState(null);
  const [updateStatus, setUpdateStatus] = useState({ status: '', adminNotes: '' });

  const handleUpdateStatus = () => {
    if (selectedReport && updateStatus.status) {
      onUpdateStatus(selectedReport._id, updateStatus.status, updateStatus.adminNotes);
      setSelectedReport(null);
      setUpdateStatus({ status: '', adminNotes: '' });
    }
  };

  return (
    <div className="space-y-6">
      {/* Filters */}
      <div className="bg-white dark:bg-gray-800 p-6 rounded-lg shadow">
        <div className="flex items-center space-x-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              Status Filter
            </label>
            <select
              value={status}
              onChange={(e) => setStatus(e.target.value)}
              className="border border-gray-300 dark:border-gray-600 rounded-md px-3 py-2 bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
            >
              <option value="">All Statuses</option>
              <option value="pending">Pending</option>
              <option value="investigating">Investigating</option>
              <option value="resolved">Resolved</option>
              <option value="dismissed">Dismissed</option>
            </select>
          </div>
        </div>
      </div>

      {/* Reports List */}
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow overflow-hidden">
        <div className="px-6 py-4 border-b border-gray-200 dark:border-gray-700">
          <h3 className="text-lg font-medium text-gray-900 dark:text-white">Reports</h3>
        </div>
        <div className="divide-y divide-gray-200 dark:divide-gray-700">
          {reports.map((report) => (
            <div key={report._id} className="p-6 hover:bg-gray-50 dark:hover:bg-gray-700">
              <div className="flex items-center justify-between">
                <div className="flex-1">
                  <div className="flex items-center space-x-3">
                    <h4 className="text-sm font-medium text-gray-900 dark:text-white">
                      Report #{report._id.slice(-6)}
                    </h4>
                    <span className={`px-2 py-1 text-xs rounded-full ${
                      report.status === 'pending' ? 'bg-yellow-100 text-yellow-800' :
                      report.status === 'investigating' ? 'bg-blue-100 text-blue-800' :
                      report.status === 'resolved' ? 'bg-green-100 text-green-800' :
                      'bg-gray-100 text-gray-800'
                    }`}>
                      {report.status}
                    </span>
                  </div>
                  <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">
                    <strong>Reason:</strong> {report.reason}
                  </p>
                  <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">
                    <strong>Description:</strong> {report.description}
                  </p>
                  <p className="text-sm text-gray-500 dark:text-gray-400 mt-2">
                    <strong>Reported User:</strong> {report.reportedUser?.username} ({report.reportedUser?.email})
                  </p>
                  <p className="text-sm text-gray-500 dark:text-gray-400">
                    <strong>Reported By:</strong> {report.reportingUser?.username} ({report.reportingUser?.email})
                  </p>
                  <p className="text-sm text-gray-500 dark:text-gray-400">
                    <strong>Date:</strong> {new Date(report.createdAt).toLocaleString()}
                  </p>
                </div>
                <button
                  onClick={() => setSelectedReport(report)}
                  className="ml-4 bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-lg text-sm"
                >
                  Update Status
                </button>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Pagination */}
      {pagination.pages > 1 && (
        <div className="flex items-center justify-between">
          <button
            onClick={() => setPage(page - 1)}
            disabled={page === 1}
            className="bg-gray-600 hover:bg-gray-700 disabled:bg-gray-400 text-white px-4 py-2 rounded-lg"
          >
            Previous
          </button>
          <span className="text-gray-600 dark:text-gray-400">
            Page {page} of {pagination.pages}
          </span>
          <button
            onClick={() => setPage(page + 1)}
            disabled={page === pagination.pages}
            className="bg-gray-600 hover:bg-gray-700 disabled:bg-gray-400 text-white px-4 py-2 rounded-lg"
          >
            Next
          </button>
        </div>
      )}

      {/* Update Status Modal */}
      {selectedReport && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white dark:bg-gray-800 p-6 rounded-lg max-w-md w-full mx-4">
            <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-4">
              Update Report Status
            </h3>
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Status
                </label>
                <select
                  value={updateStatus.status}
                  onChange={(e) => setUpdateStatus({ ...updateStatus, status: e.target.value })}
                  className="w-full border border-gray-300 dark:border-gray-600 rounded-md px-3 py-2 bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                >
                  <option value="">Select Status</option>
                  <option value="pending">Pending</option>
                  <option value="investigating">Investigating</option>
                  <option value="resolved">Resolved</option>
                  <option value="dismissed">Dismissed</option>
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Admin Notes
                </label>
                <textarea
                  value={updateStatus.adminNotes}
                  onChange={(e) => setUpdateStatus({ ...updateStatus, adminNotes: e.target.value })}
                  className="w-full border border-gray-300 dark:border-gray-600 rounded-md px-3 py-2 bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                  rows="3"
                  placeholder="Optional admin notes..."
                />
              </div>
            </div>
            <div className="flex space-x-3 mt-6">
              <button
                onClick={handleUpdateStatus}
                className="flex-1 bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-lg"
              >
                Update
              </button>
              <button
                onClick={() => {
                  setSelectedReport(null);
                  setUpdateStatus({ status: '', adminNotes: '' });
                }}
                className="flex-1 bg-gray-600 hover:bg-gray-700 text-white px-4 py-2 rounded-lg"
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

// Users Tab Component
const UsersTab = ({ users, pagination, page, setPage, search, setSearch, onToggleBan }) => {
  const [selectedUser, setSelectedUser] = useState(null);
  const [banReason, setBanReason] = useState('');

  const handleToggleBan = (user, banned) => {
    if (banned && !banReason) {
      alert('Please provide a reason for banning the user.');
      return;
    }
    onToggleBan(user._id, banned, banReason);
    setSelectedUser(null);
    setBanReason('');
  };

  return (
    <div className="space-y-6">
      {/* Search */}
      <div className="bg-white dark:bg-gray-800 p-6 rounded-lg shadow">
        <div className="flex items-center space-x-4">
          <div className="flex-1">
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              Search Users
            </label>
            <input
              type="text"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search by username or email..."
              className="w-full border border-gray-300 dark:border-gray-600 rounded-md px-3 py-2 bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
            />
          </div>
        </div>
      </div>

      {/* Users List */}
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow overflow-hidden">
        <div className="px-6 py-4 border-b border-gray-200 dark:border-gray-700">
          <h3 className="text-lg font-medium text-gray-900 dark:text-white">Users</h3>
        </div>
        <div className="divide-y divide-gray-200 dark:divide-gray-700">
          {users.map((user) => (
            <div key={user._id} className="p-6 hover:bg-gray-50 dark:hover:bg-gray-700">
              <div className="flex items-center justify-between">
                <div className="flex-1">
                  <div className="flex items-center space-x-3">
                    <h4 className="text-sm font-medium text-gray-900 dark:text-white">
                      {user.username}
                    </h4>
                    <span className={`px-2 py-1 text-xs rounded-full ${
                      !user.banned ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'
                    }`}>
                      {!user.banned ? 'Active' : 'Banned'}
                    </span>
                  </div>
                  <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">
                    {user.email}
                  </p>
                  <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
                    Level: {user.level} | XP: {user.xp} | Joined: {new Date(user.createdAt).toLocaleDateString()}
                  </p>
                </div>
                <button
                  onClick={() => setSelectedUser(user)}
                  className={`ml-4 px-4 py-2 rounded-lg text-sm ${
                    !user.banned
                      ? 'bg-red-600 hover:bg-red-700 text-white'
                      : 'bg-green-600 hover:bg-green-700 text-white'
                  }`}
                >
                  {!user.banned ? 'Ban User' : 'Unban User'}
                </button>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Pagination */}
      {pagination.pages > 1 && (
        <div className="flex items-center justify-between">
          <button
            onClick={() => setPage(page - 1)}
            disabled={page === 1}
            className="bg-gray-600 hover:bg-gray-700 disabled:bg-gray-400 text-white px-4 py-2 rounded-lg"
          >
            Previous
          </button>
          <span className="text-gray-600 dark:text-gray-400">
            Page {page} of {pagination.pages}
          </span>
          <button
            onClick={() => setPage(page + 1)}
            disabled={page === pagination.pages}
            className="bg-gray-600 hover:bg-gray-700 disabled:bg-gray-400 text-white px-4 py-2 rounded-lg"
          >
            Next
          </button>
        </div>
      )}

      {/* Ban/Unban Modal */}
      {selectedUser && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white dark:bg-gray-800 p-6 rounded-lg max-w-md w-full mx-4">
            <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-4">
              {!selectedUser.banned ? 'Ban User' : 'Unban User'}
            </h3>
            <p className="text-sm text-gray-600 dark:text-gray-400 mb-4">
              {!selectedUser.banned 
                ? `Are you sure you want to ban ${selectedUser.username}?`
                : `Are you sure you want to unban ${selectedUser.username}?`
              }
            </p>
            {!selectedUser.banned && (
              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Reason for Ban
                </label>
                <textarea
                  value={banReason}
                  onChange={(e) => setBanReason(e.target.value)}
                  className="w-full border border-gray-300 dark:border-gray-600 rounded-md px-3 py-2 bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                  rows="3"
                  placeholder="Enter reason for banning..."
                />
              </div>
            )}
            <div className="flex space-x-3">
              <button
                onClick={() => handleToggleBan(selectedUser, !selectedUser.banned)}
                className={`flex-1 px-4 py-2 rounded-lg text-white ${
                  !selectedUser.banned
                    ? 'bg-red-600 hover:bg-red-700'
                    : 'bg-green-600 hover:bg-green-700'
                }`}
              >
                {!selectedUser.banned ? 'Ban User' : 'Unban User'}
              </button>
              <button
                onClick={() => {
                  setSelectedUser(null);
                  setBanReason('');
                }}
                className="flex-1 bg-gray-600 hover:bg-gray-700 text-white px-4 py-2 rounded-lg"
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

// Feedback Tab Component
const FeedbackTab = ({ feedback, pagination, page, setPage, status, setStatus, category, setCategory, onUpdateStatus }) => {
  console.log('FeedbackTab rendered with:', { feedback, pagination });
  const [selectedFeedback, setSelectedFeedback] = useState(null);
  const [adminNotes, setAdminNotes] = useState('');

  const handleUpdateStatus = () => {
    if (selectedFeedback && adminNotes.trim()) {
      onUpdateStatus(selectedFeedback._id, 'resolved', adminNotes);
      setSelectedFeedback(null);
      setAdminNotes('');
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'pending': return 'bg-yellow-100 text-yellow-800';
      case 'reviewed': return 'bg-blue-100 text-blue-800';
      case 'resolved': return 'bg-green-100 text-green-800';
      case 'closed': return 'bg-gray-100 text-gray-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  const getCategoryIcon = (category) => {
    const icons = {
      'general': '💬',
      'ai-performance': '⚡',
      'user-interface': '🎯',
      'features': '🏆',
      'bugs': '🐛',
      'suggestions': '💡'
    };
    return icons[category] || '📝';
  };

  return (
    <div className="space-y-6">
      {/* Filters */}
      <div className="bg-white dark:bg-gray-800 p-6 rounded-lg shadow">
        <div className="flex flex-wrap gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              Status
            </label>
            <select
              value={status}
              onChange={(e) => setStatus(e.target.value)}
              className="border border-gray-300 dark:border-gray-600 rounded-md px-3 py-2 bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
            >
              <option value="">All Status</option>
              <option value="pending">Pending</option>
              <option value="reviewed">Reviewed</option>
              <option value="resolved">Resolved</option>
              <option value="closed">Closed</option>
            </select>
          </div>
          
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              Category
            </label>
            <select
              value={category}
              onChange={(e) => setCategory(e.target.value)}
              className="border border-gray-300 dark:border-gray-600 rounded-md px-3 py-2 bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
            >
              <option value="">All Categories</option>
              <option value="general">General Feedback</option>
              <option value="ai-performance">AI Performance</option>
              <option value="user-interface">User Interface</option>
              <option value="features">Features & Functionality</option>
              <option value="bugs">Bug Reports</option>
              <option value="suggestions">Feature Suggestions</option>
            </select>
          </div>
        </div>
      </div>

      {/* Feedback List */}
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow">
        <div className="p-6 border-b border-gray-200 dark:border-gray-700">
          <h3 className="text-lg font-medium text-gray-900 dark:text-white">
            Feedback ({pagination.total || 0})
          </h3>
        </div>
        
        <div className="divide-y divide-gray-200 dark:divide-gray-700">
          {feedback.map((item) => (
            <div key={item._id} className="p-6">
              <div className="flex items-start justify-between">
                <div className="flex-1">
                  <div className="flex items-center space-x-3 mb-2">
                    <span className="text-2xl">{getCategoryIcon(item.category)}</span>
                    <div>
                      <h4 className="font-medium text-gray-900 dark:text-white">
                        {item.user?.username || 'Unknown User'}
                      </h4>
                      <p className="text-sm text-gray-500 dark:text-gray-400">
                        {new Date(item.createdAt).toLocaleString()}
                      </p>
                    </div>
                  </div>
                  
                  <div className="mb-3">
                    <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getStatusColor(item.status)}`}>
                      {item.status}
                    </span>
                    {item.rating > 0 && (
                      <span className="ml-2 text-sm text-gray-600 dark:text-gray-400">
                        Rating: {item.rating}/5 ⭐
                      </span>
                    )}
                  </div>
                  
                  <p className="text-gray-700 dark:text-gray-300 mb-3">
                    {item.message}
                  </p>
                  
                  {item.adminNotes && (
                    <div className="bg-blue-50 dark:bg-blue-900/20 p-3 rounded-lg">
                      <p className="text-sm font-medium text-blue-800 dark:text-blue-200 mb-1">Admin Notes:</p>
                      <p className="text-sm text-blue-700 dark:text-blue-300">{item.adminNotes}</p>
                    </div>
                  )}
                </div>
                
                <div className="ml-4">
                  <button
                    onClick={() => setSelectedFeedback(item)}
                    className="text-primary-600 hover:text-primary-700 text-sm font-medium"
                  >
                    Update Status
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
        
        {/* Pagination */}
        {pagination.pages > 1 && (
          <div className="px-6 py-4 border-t border-gray-200 dark:border-gray-700">
            <div className="flex items-center justify-between">
              <p className="text-sm text-gray-700 dark:text-gray-300">
                Page {page} of {pagination.pages}
              </p>
              <div className="flex space-x-2">
                <button
                  onClick={() => setPage(page - 1)}
                  disabled={page <= 1}
                  className="px-3 py-1 text-sm border border-gray-300 dark:border-gray-600 rounded disabled:opacity-50"
                >
                  Previous
                </button>
                <button
                  onClick={() => setPage(page + 1)}
                  disabled={page >= pagination.pages}
                  className="px-3 py-1 text-sm border border-gray-300 dark:border-gray-600 rounded disabled:opacity-50"
                >
                  Next
                </button>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Update Status Modal */}
      {selectedFeedback && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white dark:bg-gray-800 rounded-lg p-6 max-w-md w-full mx-4">
            <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-4">
              Update Feedback Status
            </h3>
            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                Admin Notes
              </label>
              <textarea
                value={adminNotes}
                onChange={(e) => setAdminNotes(e.target.value)}
                className="w-full border border-gray-300 dark:border-gray-600 rounded-md px-3 py-2 bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                rows="4"
                placeholder="Add admin notes..."
              />
            </div>
            <div className="flex space-x-3">
              <button
                onClick={handleUpdateStatus}
                disabled={!adminNotes.trim()}
                className="flex-1 bg-primary-600 hover:bg-primary-700 text-white px-4 py-2 rounded-lg disabled:opacity-50"
              >
                Mark as Resolved
              </button>
              <button
                onClick={() => {
                  setSelectedFeedback(null);
                  setAdminNotes('');
                }}
                className="flex-1 bg-gray-600 hover:bg-gray-700 text-white px-4 py-2 rounded-lg"
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

// Activity Tab Component
const ActivityTab = ({ activity }) => {
  return (
    <div className="space-y-6">
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <div className="bg-white dark:bg-gray-800 p-6 rounded-lg shadow">
          <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-4">Recent Reports</h3>
          <div className="space-y-3">
            {activity.reports.map((report) => (
              <div key={report._id} className="flex items-center justify-between p-3 bg-gray-50 dark:bg-gray-700 rounded">
                <div>
                  <p className="text-sm font-medium text-gray-900 dark:text-white">
                    Report #{report._id.slice(-6)}
                  </p>
                  <p className="text-xs text-gray-500 dark:text-gray-400">
                    {new Date(report.createdAt).toLocaleDateString()}
                  </p>
                </div>
                <span className={`px-2 py-1 text-xs rounded-full ${
                  report.status === 'pending' ? 'bg-yellow-100 text-yellow-800' :
                  report.status === 'resolved' ? 'bg-green-100 text-green-800' :
                  'bg-gray-100 text-gray-800'
                }`}>
                  {report.status}
                </span>
              </div>
            ))}
          </div>
        </div>

        <div className="bg-white dark:bg-gray-800 p-6 rounded-lg shadow">
          <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-4">New Users</h3>
          <div className="space-y-3">
            {activity.newUsers.map((user) => (
              <div key={user._id} className="flex items-center justify-between p-3 bg-gray-50 dark:bg-gray-700 rounded">
                <div>
                  <p className="text-sm font-medium text-gray-900 dark:text-white">
                    {user.username}
                  </p>
                  <p className="text-xs text-gray-500 dark:text-gray-400">
                    {new Date(user.createdAt).toLocaleDateString()}
                  </p>
                </div>
                <span className="text-xs text-gray-500 dark:text-gray-400">
                  New
                </span>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};

export default Admin; 